import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  LayoutTemplate,
  Boxes,
  ListChecks,
  Calculator,
  MessagesSquare,
  Library,
  AlertTriangle,
  Plus,
  ArrowRight,
  Compass,
  type LucideIcon,
} from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  EmptyState,
  Input,
  PageHeader,
  Spinner,
  cn,
} from '../../ui';
import { useDashboard } from '../../features/dashboard/useDashboard';
import type { ActivityItem, ActivityKind } from '../../features/dashboard/types';

/** Per-kind icon + tint for the activity feed and metric cards. */
const KIND_META: Record<
  ActivityKind,
  { icon: LucideIcon; label: string; tone: string }
> = {
  template: { icon: LayoutTemplate, label: 'Template', tone: 'text-sky-300' },
  room: { icon: Boxes, label: 'Room', tone: 'text-emerald-300' },
  task: { icon: ListChecks, label: 'Task', tone: 'text-amber-300' },
  estimate: { icon: Calculator, label: 'Estimate', tone: 'text-accent-fg' },
  conversation: {
    icon: MessagesSquare,
    label: 'Chat',
    tone: 'text-violet-300',
  },
};

interface MetricCardProps {
  icon: LucideIcon;
  label: string;
  value: number;
  hint?: string;
  onClick: () => void;
}

function MetricCard({ icon: Icon, label, value, hint, onClick }: MetricCardProps) {
  return (
    <Card interactive onClick={onClick} className="min-w-0">
      <CardBody className="flex items-center gap-4">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-surface-3 text-accent">
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <div className="text-2xl font-semibold tabular-nums text-zinc-50">
            {value}
          </div>
          <div className="truncate text-xs text-zinc-500">
            {label}
            {hint ? <span className="text-zinc-600"> · {hint}</span> : null}
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

function relativeTime(iso: string): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffMs = Date.now() - then;
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return new Date(iso).toLocaleDateString();
}

function ActivityRow({
  item,
  onOpen,
}: {
  item: ActivityItem;
  onOpen: (href: string) => void;
}) {
  const meta = KIND_META[item.kind];
  const Icon = meta.icon;
  return (
    <button
      type="button"
      onClick={() => onOpen(item.href)}
      className="group flex w-full items-center gap-3 rounded-xl border border-transparent px-3 py-2.5 text-left transition-colors hover:border-surface-border hover:bg-surface-2"
    >
      <div
        className={cn(
          'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-surface-3',
          meta.tone,
        )}
      >
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-zinc-200">
          {item.title || 'Untitled'}
        </div>
        <div className="flex items-center gap-2 text-xs text-zinc-500">
          <Badge tone="neutral">{meta.label}</Badge>
          {item.subtitle && <span className="truncate">{item.subtitle}</span>}
        </div>
      </div>
      <span className="shrink-0 text-xs text-zinc-500">
        {relativeTime(item.timestamp)}
      </span>
      <ArrowRight className="h-4 w-4 shrink-0 text-zinc-600 transition-colors group-hover:text-zinc-300" />
    </button>
  );
}

/** Suggested starter questions shown as one-tap chips in the hero. */
const ASK_SUGGESTIONS = [
  'What is blocked right now?',
  "What's the status of our top goals?",
  'Catch me up since last week',
  'Who is overloaded this sprint?',
];

/**
 * Ask Vector hero — the front-and-center prompt at the top of the landing page.
 * Submitting the input or tapping a suggestion chip deep-links to the Ask Vector
 * page with the question pre-filled and auto-asked (via router state).
 */
export function AskVectorHero() {
  const navigate = useNavigate();
  const [question, setQuestion] = useState('');

  const goAsk = (raw: string) => {
    const text = raw.trim();
    if (!text) return;
    navigate('/ask', { state: { initialQuestion: text } });
  };

  return (
    <section
      aria-label="Ask Vector"
      className="relative mb-6 overflow-hidden rounded-2xl border border-accent/30 bg-gradient-to-br from-accent/15 via-surface-2 to-surface-1 p-6 shadow-card"
    >
      <div className="flex items-center gap-2 text-accent-fg">
        <Compass className="h-5 w-5" aria-hidden="true" />
        <span className="text-xs font-semibold uppercase tracking-wide">
          Ask Vector
        </span>
      </div>
      <h2 className="mt-2 text-xl font-semibold tracking-tight text-zinc-50">
        Ask anything about your team
      </h2>
      <p className="mt-1 max-w-2xl text-sm text-zinc-400">
        Vector retrieves the most relevant goals, work, documents and decisions —
        then answers with links back to every source.
      </p>

      <form
        className="mt-4 flex flex-col gap-2 sm:flex-row"
        onSubmit={(e) => {
          e.preventDefault();
          goAsk(question);
        }}
      >
        <div className="flex-1">
          <Input
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="What is blocked right now?"
            aria-label="Ask Vector a question"
          />
        </div>
        <Button type="submit" rightIcon={<ArrowRight className="h-4 w-4" />}>
          Ask
        </Button>
      </form>

      <div className="mt-3 flex flex-wrap gap-2">
        {ASK_SUGGESTIONS.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => goAsk(s)}
            className="rounded-full border border-surface-border bg-surface-2 px-3 py-1.5 text-xs text-zinc-300 transition-colors hover:border-accent/40 hover:bg-surface-3 hover:text-zinc-100"
          >
            {s}
          </button>
        ))}
      </div>
    </section>
  );
}

/**
 * Dashboard landing page. Summarizes counts (templates / rooms / tasks /
 * estimates) and a recent-activity feed merged across features. Reaches data
 * only through the useDashboard hook (which composes the feature stores).
 */
/**
 * Dashboard body (metric cards + recent activity + quick actions), WITHOUT the
 * Ask hero or PageHeader. Extracted so the unified Home page can compose the
 * workspace overview into its "Me" tab without duplicating the useDashboard
 * logic. Reaches data only through the useDashboard hook.
 */
export function DashboardContent() {
  const navigate = useNavigate();
  const { counts, activity, loading, error } = useDashboard();

  const metrics = useMemo(
    () => [
      {
        icon: LayoutTemplate,
        label: 'Templates',
        value: counts.templates,
        hint:
          counts.templateVersions > counts.templates
            ? `${counts.templateVersions} versions`
            : undefined,
        href: '/templates',
      },
      { icon: Boxes, label: 'Rooms', value: counts.rooms, href: '/rooms' },
      { icon: ListChecks, label: 'Tasks', value: counts.tasks, href: '/tasks' },
      {
        icon: Calculator,
        label: 'Estimates',
        value: counts.estimates,
        href: '/estimation',
      },
    ],
    [counts],
  );

  const isEmpty =
    !loading &&
    !error &&
    counts.templates === 0 &&
    counts.rooms === 0 &&
    counts.tasks === 0 &&
    counts.estimates === 0 &&
    counts.conversations === 0;

  const quickActions: { label: string; icon: LucideIcon; to: string }[] = [
    { label: 'New template', icon: LayoutTemplate, to: '/templates/new' },
    { label: 'New estimate', icon: Calculator, to: '/estimation/new' },
    { label: 'Open Knowledge', icon: Library, to: '/knowledge' },
    { label: 'Start a chat', icon: MessagesSquare, to: '/chat' },
  ];

  let body: ReactNode;
  if (loading) {
    body = (
      <div className="flex items-center justify-center py-24">
        <Spinner label="Loading dashboard" />
      </div>
    );
  } else if (error) {
    body = (
      <EmptyState
        icon={<AlertTriangle className="h-6 w-6" />}
        title="Couldn't load the dashboard"
        description={error}
      />
    );
  } else if (isEmpty) {
    body = (
      <EmptyState
        icon={<LayoutDashboard className="h-6 w-6" />}
        title="Your workspace is empty"
        description="Create a template, spin up a room, or start a chat — activity will show up here as you go."
        action={
          <Button
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={() => navigate('/templates/new')}
          >
            Create a template
          </Button>
        }
      />
    );
  } else {
    body = (
      <div className="space-y-6">
        {/* Metric cards */}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {metrics.map((m) => (
            <MetricCard
              key={m.label}
              icon={m.icon}
              label={m.label}
              value={m.value}
              hint={m.hint}
              onClick={() => navigate(m.href)}
            />
          ))}
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Recent activity */}
          <Card className="lg:col-span-2">
            <CardBody>
              <div className="mb-2 flex items-center justify-between">
                <h2 className="text-sm font-semibold text-zinc-100">
                  Recent activity
                </h2>
                {counts.conversations > 0 && (
                  <span className="text-xs text-zinc-500">
                    {counts.conversations}{' '}
                    {counts.conversations === 1 ? 'conversation' : 'conversations'}
                  </span>
                )}
              </div>
              {activity.length === 0 ? (
                <p className="py-8 text-center text-sm text-zinc-500">
                  No recent activity yet.
                </p>
              ) : (
                <div className="space-y-0.5">
                  {activity.map((item) => (
                    <ActivityRow
                      key={item.key}
                      item={item}
                      onOpen={(href) => navigate(href)}
                    />
                  ))}
                </div>
              )}
            </CardBody>
          </Card>

          {/* Quick actions */}
          <Card>
            <CardBody>
              <h2 className="mb-3 text-sm font-semibold text-zinc-100">
                Quick actions
              </h2>
              <div className="grid gap-2">
                {quickActions.map((a) => {
                  const Icon = a.icon;
                  return (
                    <Button
                      key={a.to}
                      variant="outline"
                      className="justify-start"
                      leftIcon={<Icon className="h-4 w-4" />}
                      onClick={() => navigate(a.to)}
                    >
                      {a.label}
                    </Button>
                  );
                })}
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    );
  }

  return <>{body}</>;
}

/**
 * Dashboard landing page (legacy standalone route). The unified Home page now
 * owns `/`; this wrapper is retained for completeness and composes the shared
 * {@link AskVectorHero} + {@link DashboardContent}.
 */
export function DashboardPage() {
  return (
    <div>
      <AskVectorHero />
      <PageHeader
        icon={<LayoutDashboard className="h-5 w-5" />}
        title="Dashboard"
        description="Overview of your estimation, work-tracking, knowledge, and chat activity."
      />
      <DashboardContent />
    </div>
  );
}

export default DashboardPage;
